import { Phone, MapPin, Clock } from "lucide-react";
import { Button } from "../components/ui/button";
import { RESTAURANT } from "../lib/restaurant";
import { useLang } from "../i18n/LanguageContext";

export default function Contact() {
  const { t } = useLang();
  return (
    <div data-testid="contact-page" className="pt-20">
      <section className="bg-charcoal text-cream py-24 sm:py-28 border-b border-white/10">
        <div className="max-w-[1400px] mx-auto px-5 sm:px-8 lg:px-12">
          <p className="uppercase-track text-xs text-gold mb-6">{t.contact.eyebrow}</p>
          <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl leading-[1.05] mb-6">
            {t.contact.title1}<br />
            <span className="italic text-gold">{t.contact.title2}</span>
          </h1>
          <p className="text-cream/85 text-lg max-w-2xl leading-relaxed">{t.contact.lead}</p>
        </div>
      </section>

      <section className="bg-cream py-20">
        <div className="max-w-[1400px] mx-auto px-5 sm:px-8 lg:px-12 grid lg:grid-cols-12 gap-12">
          {/* Left: info */}
          <div className="lg:col-span-5 space-y-10">
            <div>
              <h2 className="font-serif text-3xl text-charcoal mb-6">{RESTAURANT.name}</h2>
              <div className="space-y-5 text-charcoal/85">
                <div className="flex gap-4">
                  <MapPin className="h-5 w-5 text-wine mt-1 shrink-0" />
                  <div>
                    <p className="font-medium text-charcoal">{t.contact.address}</p>
                    <p>{RESTAURANT.address.street}<br />{RESTAURANT.address.postal}<br />{RESTAURANT.address.district}</p>
                  </div>
                </div>
                <div className="flex gap-4">
                  <Phone className="h-5 w-5 text-wine mt-1 shrink-0" />
                  <div>
                    <p className="font-medium text-charcoal">{t.contact.phone}</p>
                    <a href={`tel:${RESTAURANT.phoneRaw}`} data-testid="contact-phone-link" className="hover:text-wine transition">{RESTAURANT.phone}</a>
                  </div>
                </div>
                <div className="flex gap-4">
                  <Clock className="h-5 w-5 text-wine mt-1 shrink-0" />
                  <div className="w-full">
                    <p className="font-medium text-charcoal mb-2">{t.contact.hours}</p>
                    <ul className="space-y-1.5 text-sm">
                      {t.hours.map((h) => (
                        <li key={h.day} className="flex justify-between gap-4 border-b border-charcoal/10 pb-1.5">
                          <span className="text-charcoal/85">{h.day}</span>
                          <span className="text-charcoal">{h.value}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            </div>

            <div className="flex flex-wrap gap-3">
              <a href={`tel:${RESTAURANT.phoneRaw}`}>
                <Button data-testid="contact-call-button" className="rounded-none bg-wine hover:bg-wine/85 text-cream uppercase-track text-xs h-12 px-7">
                  <Phone className="h-4 w-4 mr-2" /> {t.common.callNow}
                </Button>
              </a>
              <a href={RESTAURANT.mapsLink} target="_blank" rel="noopener noreferrer">
                <Button data-testid="contact-directions" variant="outline" className="rounded-none bg-transparent border-charcoal text-charcoal hover:bg-charcoal hover:text-cream uppercase-track text-xs h-12 px-7">
                  {t.common.directions}
                </Button>
              </a>
            </div>
          </div>

          {/* Right: call-only card */}
          <div className="lg:col-span-7">
            <div className="bg-charcoal text-cream p-10 sm:p-14 mb-10 text-center">
              <h2 className="font-serif text-4xl mb-4">{t.contact.callCardTitle}</h2>
              <p className="text-cream/85 mb-8 max-w-md mx-auto">{t.contact.callCardText}</p>
              <a href={`tel:${RESTAURANT.phoneRaw}`} className="block font-serif text-4xl sm:text-5xl text-gold hover:text-cream transition mb-8">
                {RESTAURANT.phone}
              </a>
              <a href={`tel:${RESTAURANT.phoneRaw}`}>
                <Button data-testid="contact-call-cta" className="rounded-none bg-wine hover:bg-wine/85 text-cream uppercase-track text-xs h-12 px-10">{t.common.callNow}</Button>
              </a>
            </div>

            <div className="aspect-[16/10] overflow-hidden border border-charcoal/10" data-testid="contact-map">
              <iframe
                title="Segreto Ristorante Italiano map"
                src={RESTAURANT.mapsEmbed}
                className="w-full h-full"
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
